/** Mirrors hub `structuredSnapshotDrift` for client-side History drift display. */

type QueryColumnMeta = { name: string };

type QueryResultsPayload = {
  columns: QueryColumnMeta[];
  rows: unknown[][];
};

export type StructuredSnapshotDrift = {
  total_cells: number;
  changed_cells: number;
  added_rows: number;
  removed_rows: number;
  changes: Array<{ row: number; column: string; old_value: unknown; new_value: unknown }>;
};

function sameCell(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Number.isNaN(a as number) && Number.isNaN(b as number)) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function colIndexMap(columns: QueryColumnMeta[]): Map<string, number> {
  const m = new Map<string, number>();
  for (let i = 0; i < columns.length; i++) {
    const n = columns[i]!.name;
    if (!m.has(n)) m.set(n, i);
  }
  return m;
}

export function computeStructuredSnapshotDrift(
  previous: QueryResultsPayload | null | undefined,
  current: QueryResultsPayload | null | undefined
): StructuredSnapshotDrift | null {
  if (!current || !previous) return null;
  if (previous.rows.length === 0 && current.rows.length === 0) return null;

  const prevCols = previous.columns;
  const currMap = colIndexMap(current.columns);
  const nPrev = previous.rows.length;
  const nCurr = current.rows.length;
  const aligned = Math.min(nPrev, nCurr);
  const added_rows = Math.max(0, nCurr - nPrev);
  const removed_rows = Math.max(0, nPrev - nCurr);

  let total_cells = 0;
  let changed_cells = 0;
  const changes: StructuredSnapshotDrift["changes"] = [];

  for (let row = 0; row < aligned; row++) {
    const prevRow = previous.rows[row] ?? [];
    const currRow = current.rows[row] ?? [];
    for (let pi = 0; pi < prevCols.length; pi++) {
      const name = prevCols[pi]!.name;
      const cj = currMap.get(name);
      total_cells += 1;
      const oldVal = prevRow[pi];
      const newVal = cj !== undefined ? currRow[cj] : undefined;
      if (!sameCell(oldVal, newVal)) {
        changed_cells += 1;
        changes.push({ row, column: name, old_value: oldVal, new_value: newVal });
      }
    }
  }

  if (changed_cells === 0 && added_rows === 0 && removed_rows === 0) return null;

  return { total_cells, changed_cells, added_rows, removed_rows, changes };
}
